import * as React from 'react';
import { BaseForm } from './Base';
import theme from '../../utils/theme';
import { Category } from '../../product';
import { useRequest, getHeaders } from '../../api/client';
import { ProfileManager, ProfileContext } from '../../profile';
import Loader from '../Loader';

type SelectProps = React.InputHTMLAttributes<HTMLSelectElement>;

export interface InputProps extends SelectProps {
  id: string;
  label: string;
  tooltip?: string;
  field: any;
  onValueChange: Function;
  defaultValue: any;
}

const addToArray = (value: any, newValue: string) => {
  return Array.from(new Set(value.concat([newValue])));
};

const categories = (context: ProfileManager) => {
  const [state, _] = useRequest(() => ({
    method: 'GET',
    url: '/store/categories',
    headers: getHeaders(context),
  }));

  return state;
};

const getName = (id: string, options: Category[]) => {
  const item = options.find(
    (item: Category) => item.id.toLowerCase() === id.toLowerCase(),
  );

  if (!item) {
    return 'N/A';
  }

  return item.name;
};

const getImage = (id: string, options: Category[]) => {
  const item = options.find(
    (item: Category) => item.id.toLowerCase() === id.toLowerCase(),
  );

  if (!item) {
    return null;
  }

  return item.image_url;
};

export default function ProductCategorySelect({
  label,
  id,
  tooltip,
  field,
  onValueChange,
  defaultValue = [],
}: InputProps) {
  const context = React.useContext(ProfileContext);
  const state = categories(context);
  const [newValue, setNewValue] = React.useState<string>(null);
  const inputRef = React.useRef(null);

  if (state.loading) {
    return <Loader />;
  }

  const onAdd = () => {
    if (!newValue) {
      return;
    }

    onValueChange(field, addToArray(defaultValue, newValue));
    inputRef.current.selectedIndex = 0;
  };

  const renderItem = (options: Category[]) => (categoryId: string) => (
    <div
      className={`${theme.form.field.input} flex justify-between items-center flex-column w-auto mr2 mb2`}
    >
      <img
        className="w4 h4 db pa1 pa3-ns br-100"
        src={getImage(categoryId, options)}
        alt=""
      />
      <span className="ph1 ph3-ns lh-copy">{getName(categoryId, options)}</span>
      <button
        type="button"
        className="red b dib link bg-transparent bw0 pointer dim ma0 pa1 pa3-ns"
        onClick={() => {
          onValueChange(
            field,
            defaultValue.filter((arrItem: string) => arrItem !== categoryId),
          );
        }}
      >
        ✕
      </button>
    </div>
  );

  return (
    <BaseForm label={label} id={id} tooltip={tooltip}>
      <div className="relative mv3">
        <select
          className={theme.form.field.input}
          ref={inputRef}
          onChange={(e) => setNewValue(e.target.value)}
        >
          <option>Please Select</option>
          {state.data &&
            state.data.map((category: Category) => (
              <option value={category.id}>{category.name}</option>
            ))}
        </select>
        <button
          type="button"
          className="gold b dib link bg-transparent bw0 pointer dim absolute top-0 right-0 pa3"
          onClick={onAdd}
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap">
        {defaultValue.map(renderItem(state.data || []))}
      </div>
    </BaseForm>
  );
}
